import React, { useEffect, useState, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useExperienceStore } from './experienceStore'
import { ExperienceRegistry } from './ExperienceRegistry'
import { getExperienceContext } from './ContextAnalyzer'
import { checkEligibility } from './EligibilityRules'
import { resolveWinningExperience } from './PriorityEngine'
import { EventBus } from './EventBus'
import { useAuth } from '@/platform/auth/useAuth'
import { useDashboard } from '@/features/dashboard/hooks/useDashboard'
import { SkipControl } from '../components/shared/SkipControl'
import type { ExperienceContext, ExperienceManifest } from '../types'

// ── Experience Error Boundary ──────────────────────────────────────
interface BoundaryProps {
  children: React.ReactNode
  onError: (error: Error) => void
}

class ExperienceErrorBoundary extends React.Component<BoundaryProps, { hasError: boolean }> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    this.props.onError(error)
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

// ── Main Experience Engine ──────────────────────────────────────────
export function ExperienceEngine() {
  const { user } = useAuth()
  const { data: dashboardSummary } = useDashboard()

  const {
    activeExperienceId,
    lifecycleState,
    settings,
    setActiveExperienceId,
    setLifecycleState,
    recordRun,
    recordSkip,
    recordCompletion,
    incrementVisitCount,
  } = useExperienceStore()

  const [activeExp, setActiveExp] = useState<ExperienceManifest | null>(null)
  const [ActiveComponent, setActiveComponent] = useState<React.ComponentType<any> | null>(null)
  const [solvedContext, setSolvedContext] = useState<ExperienceContext | null>(null)

  // Observability timings
  const [preloadStart, setPreloadStart] = useState<number>(0)
  const [preloadDuration, setPreloadDuration] = useState<number>(0)
  const [playbackStart, setPlaybackStart] = useState<number>(0)

  // Increment visit count on initial mount
  useEffect(() => {
    incrementVisitCount()
  }, [])

  // Orchestrator loop: evaluates and triggers experiences
  useEffect(() => {
    if (lifecycleState !== 'idle' && activeExperienceId) return // Already active

    // 1. Check if user turned entrance experiences off
    if (settings.mode === 'off') {
      return
    }

    // 2. Novelty Guard: 20% chance to show no experience at all to preserve novelty
    if (settings.mode === 'surprise' && Math.random() < 0.20) {
      console.log('[ExperienceEngine] Novelty preservation active. Bypassing experience.')
      return
    }

    // 3. Solve current context
    const context = getExperienceContext(user, dashboardSummary)
    setSolvedContext(context)

    // 4. Resolve active experience
    let targetExp: ExperienceManifest | null = null

    if (settings.mode === 'surprise') {
      const allExps = ExperienceRegistry.getAll()
      const eligibleExps = allExps.filter((exp) =>
        checkEligibility(exp, context, {
          disabledIds: settings.disabled,
        })
      )
      targetExp = resolveWinningExperience(eligibleExps, context, settings.favorites)
    } else {
      const selected = ExperienceRegistry.get(settings.mode)
      if (selected && !settings.disabled.includes(selected.metadata.id)) {
        targetExp = selected
      }
    }

    if (!targetExp) {
      return
    }

    // 5. Start preload phase & record telemetry start
    setPreloadStart(Date.now())
    setActiveExp(targetExp)
    setActiveExperienceId(targetExp.metadata.id)
    setLifecycleState('preloading')
  }, [user, dashboardSummary, settings.mode, activeExperienceId])

  // Lifecycle execution: executes preload & renders components
  useEffect(() => {
    if (!activeExp || !activeExperienceId) return

    let isSubscribed = true

    const runPreloadAndPlay = async () => {
      try {
        const context = solvedContext || getExperienceContext(user, dashboardSummary)

        // Step A: Preload assets
        if (activeExp.preload) {
          await activeExp.preload(context)
        }
        
        const preloadEnd = Date.now()
        const duration = preloadEnd - preloadStart
        
        if (isSubscribed) {
          setPreloadDuration(duration)
          setPlaybackStart(preloadEnd)
        }

        EventBus.publish('RESOURCES_LOADED', activeExperienceId, { preloadDuration: duration })

        // Step B: Dynamically import components
        const module = await activeExp.ComponentLoader()
        if (!isSubscribed) return

        setActiveComponent(() => module.default)
        setLifecycleState('entering')
        recordRun(activeExperienceId)
        EventBus.publish('STARTED', activeExperienceId)

        // Wait brief entering transition delay
        setTimeout(() => {
          if (isSubscribed) {
            setLifecycleState('playing')
          }
        }, 300)
      } catch (err) {
        handleError(err instanceof Error ? err : new Error(String(err)))
      }
    }

    if (lifecycleState === 'preloading') {
      runPreloadAndPlay()
    }

    return () => {
      isSubscribed = false
    }
  }, [activeExp, lifecycleState])

  const cleanup = () => {
    if (activeExp && activeExp.cleanup) {
      try {
        activeExp.cleanup()
      } catch (err) {
        console.error(`[ExperienceEngine] Cleanup failed:`, err)
      }
    }
    setActiveComponent(null)
    setActiveExp(null)
    setActiveExperienceId(null)
    setLifecycleState('idle')
  }

  const handleError = (error: Error) => {
    console.error(`[ExperienceEngine] Recovery boundary triggered:`, error)
    if (activeExperienceId) {
      EventBus.publish('FAILED', activeExperienceId, {
        error: error.message,
        stack: error.stack,
      })
    }
    cleanup() // Gracefully close overlay and let user enter workspace
  }

  const handleSkip = () => {
    if (!activeExperienceId) return
    const playbackDuration = Date.now() - playbackStart
    recordSkip(activeExperienceId)
    EventBus.publish('SKIPPED', activeExperienceId, {
      preloadDuration,
      playbackDuration,
      totalDuration: preloadDuration + playbackDuration,
    })
    setLifecycleState('exiting')
    setTimeout(cleanup, 400)
  }

  const handleComplete = () => {
    if (!activeExperienceId) return
    const playbackDuration = Date.now() - playbackStart
    recordCompletion(activeExperienceId)
    EventBus.publish('COMPLETED', activeExperienceId, {
      preloadDuration,
      playbackDuration,
      totalDuration: preloadDuration + playbackDuration,
    })
    setLifecycleState('exiting')
    setTimeout(cleanup, 400)
  }

  // Escape key shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && lifecycleState === 'playing') {
        handleSkip()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lifecycleState, activeExperienceId, playbackStart])

  const showOverlay = lifecycleState !== 'idle' && activeExp && solvedContext

  return (
    <AnimatePresence>
      {showOverlay && (
        <motion.div
          id="nttd-experience-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#0a0a0b',
            zIndex: 400,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <SkipControl onSkip={handleSkip} isVisible={lifecycleState === 'playing'} />

          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {ActiveComponent && (
              <ExperienceErrorBoundary onError={handleError}>
                <Suspense fallback={null}>
                  <ActiveComponent
                    onComplete={handleComplete}
                    context={solvedContext}
                  />
                </Suspense>
              </ExperienceErrorBoundary>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default ExperienceEngine
